import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, EmptyState, Spinner, TextField, Typography } from '@glplus/glpweb-dsl';
import { AdminApiError, adminApiClient, type VendorOwnershipRequestItem } from '../../lib/adminApiClient';
import { DANGER_BTN, NEUTRAL_BTN } from '../../lib/buttonStyles';
import { Actions, Card, Field, MUTED, PRIMARY_BTN, Panel, SideBySide, day } from './parts';

export function ownershipRequestLabel(item: Pick<VendorOwnershipRequestItem, 'requestType'>): string {
  return item.requestType === 'portal_owner_transfer' ? 'Portal owner transfer' : 'Legal company ownership change';
}

function person(person: { email: string; givenName: string | null; familyName: string | null; status: string } | null): string {
  if (!person) return '—';
  const name = [person.givenName, person.familyName].filter(Boolean).join(' ');
  return `${name || person.email} · ${person.email} · ${person.status}`;
}

function detailValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map(detailValue).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function OwnershipTab({ canReview, onCount }: { canReview: boolean; onCount: (n: number) => void }) {
  const [items, setItems] = useState<VendorOwnershipRequestItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await adminApiClient.listVendorOwnershipRequests('open');
      setItems(result.items);
      onCount(result.count);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ownership requests');
    }
  }, [onCount]);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!items) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><Spinner color="brand" /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {flash && <Alert severity="success">{flash}</Alert>}
      {items.length === 0 ? (
        <EmptyState icon="manage_accounts" title="No ownership requests waiting" body="Portal transfers and legal company ownership changes stay here until staff reviews them." />
      ) : items.map((item) => (
        <OwnershipCard key={item.requestId} item={item} canReview={canReview} onDone={(message) => { setFlash(message); void load(); }} />
      ))}
    </Box>
  );
}

function OwnershipCard({ item, canReview, onDone }: { item: VendorOwnershipRequestItem; canReview: boolean; onDone: (message: string) => void }) {
  const [mode, setMode] = useState<'reject' | 'information' | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const label = ownershipRequestLabel(item);
  const legalChange = item.requestType === 'legal_ownership_change';

  const run = async (work: () => Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      onDone(done);
    } catch (err) {
      setError(err instanceof AdminApiError ? err.message : 'That did not go through');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card testId={`vendor-ownership-${item.requestId}`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700 }}>{label}</Typography>
        <Chip label={item.vendor.businessName} size="small" />
        <Chip label={item.status === 'needs_information' ? 'Information requested' : 'Waiting'} size="small" />
      </Box>
      <Typography sx={{ fontSize: 13, color: MUTED }}>Submitted {day(item.submittedAt)} by {person(item.requestedBy)}</Typography>

      <SideBySide
        leftTitle="Ownership request"
        rightTitle="Staff checks"
        left={(
          <>
            <Field label="Request type">{label}</Field>
            <Field label="Current portal owner">{item.vendor.ownerVendorUserId === item.requestedByVendorUserId ? person(item.requestedBy) : item.vendor.ownerVendorUserId}</Field>
            <Field label="Requested new portal owner">{person(item.targetUser)}</Field>
            {Object.entries(item.details).map(([key, value]) => <Field key={key} label={key.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ')}>{detailValue(value)}</Field>)}
          </>
        )}
        right={legalChange ? (
          <>
            <Field label="Company review">Approval restarts the full onboarding and review process because this is effectively a new company.</Field>
            <Field label="Portal control">The current portal owner stays in place during onboarding.</Field>
            <Field label="Go-live">Each location and its products must be set up before that location can be live.</Field>
          </>
        ) : (
          <>
            <Field label="Target membership">{item.targetUser?.status === 'active' ? 'Active Vendor team member' : 'Target is not active'}</Field>
            <Field label="Current owner after approval">Becomes a manager; the account is not removed.</Field>
            <Field label="Company status">Unchanged — this transfers portal control only.</Field>
          </>
        )}
      />

      {legalChange && <Box sx={{ mt: 2 }}><Alert severity="warning">Approving this request returns the Vendor to onboarding. It does not install a new legal owner immediately.</Alert></Box>}
      {error && <Box sx={{ mt: 2 }}><Alert severity="error">{error}</Alert></Box>}
      {canReview && (
        <Actions>
          <Button ariaLabel={`Approve ${label}`} buttonText={busy ? 'Working…' : legalChange ? 'Approve and restart onboarding' : 'Approve transfer'} variant="contained" size="small" disabled={busy} sx={PRIMARY_BTN} onClick={() => void run(() => adminApiClient.decideVendorOwnershipRequest(item.requestId, { decision: 'approved' }), legalChange ? `${item.vendor.businessName} returned to onboarding.` : `Portal ownership transferred for ${item.vendor.businessName}.`)} />
          <Button ariaLabel={`Request more information for ${label}`} buttonText="Need more information" variant="outlined" size="small" disabled={busy} sx={NEUTRAL_BTN} onClick={() => setMode(mode === 'information' ? null : 'information')} />
          <Button ariaLabel={`Reject ${label}`} buttonText="Reject" variant="outlined" size="small" disabled={busy} sx={DANGER_BTN} onClick={() => setMode(mode === 'reject' ? null : 'reject')} />
        </Actions>
      )}
      {mode && (
        <Panel title={mode === 'information' ? 'What information is missing?' : 'Why is this request rejected?'}>
          <TextField label="The Vendor reads this" ariaLabel="Ownership review note" multiline minRows={2} value={note} onChange={(event) => setNote(event.target.value)} />
          <Box sx={{ mt: 1.5 }}>
            <Button ariaLabel="Save ownership decision" buttonText={mode === 'information' ? 'Request information' : 'Reject request'} variant="contained" size="small" disabled={busy || !note.trim()} sx={mode === 'information' ? PRIMARY_BTN : DANGER_BTN} onClick={() => void run(() => adminApiClient.decideVendorOwnershipRequest(item.requestId, { decision: mode === 'information' ? 'needs_information' : 'rejected', note: note.trim() }), mode === 'information' ? `Requested more information from ${item.vendor.businessName}.` : `Rejected the ownership request for ${item.vendor.businessName}.`)} />
          </Box>
        </Panel>
      )}
    </Card>
  );
}

